import type { PostHogConfig, Properties } from "posthog-js";
import posthog from "posthog-js";
import type { BrowserAnalyticsEventName } from "./events/names";

const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

export type {
  AnalyticsEventName,
  BrowserAnalyticsEventName,
  ServerAnalyticsEventName,
} from "./events/names";

export type InitBrowserAnalyticsInput = {
  apiHost?: string;
  debug?: boolean;
  host?: string;
  key: string;
};

function detectBrowserEnvironment(): string {
  if (process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV) {
    return process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV;
  }

  if (typeof window === "undefined") {
    return "unknown";
  }

  const hostname = window.location.hostname;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "development";
  }

  if (hostname.includes("preview.")) {
    return "preview";
  }

  if (hostname.includes("vercel.app")) {
    return "preview";
  }

  return "production";
}

export function getBrowserAnalyticsClient() {
  return posthog;
}

export function initBrowserAnalytics({
  apiHost = "https://t.vacwork.app",
  debug = false,
  host = DEFAULT_POSTHOG_HOST,
  key,
}: InitBrowserAnalyticsInput) {
  if (typeof window === "undefined") {
    return posthog;
  }

  const config = {
    api_host: apiHost,
    ui_host: host,
    defaults: "2025-05-24",
    loaded: (instance) => {
      if (debug) {
        instance.debug(false);
      }
      instance.register({
        environment: detectBrowserEnvironment(),
      });
    },
  } satisfies Partial<PostHogConfig>;

  posthog.init(key, config);
  return posthog;
}

export function captureBrowserEvent(
  event: BrowserAnalyticsEventName,
  properties?: Record<string, unknown>
) {
  posthog.capture(event, properties);
}

export function captureBrowserException(error: unknown, context?: Properties) {
  posthog.captureException(error, {
    environment: detectBrowserEnvironment(),
    ...context,
  });
}

export function getBrowserDistinctId() {
  return posthog.get_distinct_id();
}

export function identifyBrowserActor(
  distinctId: string,
  properties?: Record<string, unknown>
) {
  posthog.identify(distinctId, properties);
}

export function groupBrowserAnalytics(
  groupType: string,
  groupKey: string,
  properties?: Record<string, unknown>
) {
  posthog.group(groupType, groupKey, properties);
}

export function resetBrowserAnalytics() {
  posthog.reset();
}
